/**
 * Thin fetch wrapper that talks to our /api/* endpoints and includes
 * cookies (so the JWT in `dataflow_session` rides along).
 *
 * Vite's dev server proxies `/api` → `http://localhost:3000` (see
 * vite.config.web.ts), and in production Fastify serves both the API
 * and the static SPA from the same origin, so a relative `/api/…`
 * works in both modes.
 */
export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  // Only declare a JSON content-type when we actually send a body. Fastify's
  // default JSON parser rejects empty bodies with FST_ERR_CTP_EMPTY_JSON_BODY
  // when the header is present, which silently breaks bodyless POSTs (logout,
  // toggle, run-now, etc.).
  //
  // FormData / Blob bodies need the browser-set multipart boundary — don't
  // override Content-Type for those.
  const hasBody = init.body !== undefined && init.body !== null;
  const isMultipart =
    typeof FormData !== "undefined" && init.body instanceof FormData;
  const baseHeaders: Record<string, string> =
    hasBody && !isMultipart ? { "Content-Type": "application/json" } : {};
  const res = await fetch(path.startsWith("/") ? path : `/api/${path}`, {
    credentials: "include",
    ...init,
    headers: { ...baseHeaders, ...(init.headers ?? {}) },
  });
  if (!res.ok) {
    // Read the body as text once, then attempt JSON.parse — `res.json()`
    // and `res.text()` both consume the stream, so calling one after
    // the other throws "body stream already read".
    const raw = await res.text().catch(() => "");
    let body: unknown = raw;
    if (raw) {
      try { body = JSON.parse(raw); } catch { /* keep raw text */ }
    }
    const err = new Error(`API ${res.status} ${path}`) as Error & {
      status: number;
      body: unknown;
    };
    err.status = res.status;
    err.body = body;
    throw err;
  }
  // 204 No Content
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Sentinel for `api()` errors so callers can `instanceof`-check status. */
export function isApiError(e: unknown): e is Error & { status: number; body: unknown } {
  return e instanceof Error && typeof (e as { status?: unknown }).status === "number";
}
